import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ReservationForm from '../components/ReservationForm';
import PoweredBy from '../components/PoweredBy';
import { getRestaurantById } from '../services/restaurantService';
import { fetchRestaurantBySlug } from '../services/guestPortalService';
import { getEventById } from '../services/eventService';
import { getShiftById } from '../services/shiftService';
import { normalizeNoteHtml } from '../utils/richText';
import { setPageSeo } from '../utils/seo';
import './ReservationWidget.css';
import '../styles/widget-mobile.css';
import '../styles/widget-v2.css';

const BASE_API = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';

/**
 * ReservationWidget - Standalone widget page for embedding in external websites via iframe
 * This page contains only the reservation form without navbar, footer, or any other UI elements
 * Query params:
 * - restaurantId: The ID of the restaurant (required)
 * - eventId / shiftId: optional pre-selection
 * - primaryColor / secondaryColor / accentColor / textColor / backgroundColor:
 *     optional hex overrides for the widget theme (fall back to the restaurant's
 *     own widget colors when absent)
 *
 * - embed: pass "1" to opt into host-embed mode — the widget then blends into
 *     the host page (transparent background, no outer padding) and posts
 *     "eez7-reservation" messages to the parent frame (type: 'resize' with a
 *     pixel height, and 'submitted' on success) so the host can size the frame to
 *     fit. Existing plain iframe embeds that don't pass this flag are unaffected.
 */
const post = (msg) => {
  try {
    if (typeof window !== 'undefined' && window.parent && window.parent !== window) {
      window.parent.postMessage({ source: 'eez7-reservation', ...msg }, '*');
    }
  } catch (e) {
    /* noop */
  }
};

const ReservationWidget = () => {
  const [searchParams] = useSearchParams();
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isEventDate, setIsEventDate] = useState(false);
  const [eventHouseRules, setEventHouseRules] = useState('');
  // On experience dates: null until the guest picks a booking type, then
  // 'regular' or 'tickets'. Drives which top-of-widget note is shown.
  const [bookingChoice, setBookingChoice] = useState(null);
  const [initialEvent, setInitialEvent] = useState(null);
  const [eventAccessDenied, setEventAccessDenied] = useState(null);
  // initialEventShift is consumed once by the form for pre-selection (then cleared);
  // displayEventShift is the persistent banner data shown in the widget UI.
  const [initialEventShift, setInitialEventShift] = useState(null);
  const [displayEventShift, setDisplayEventShift] = useState(null);

  const [prefillUser, setPrefillUser] = useState(null);

  // Two ways in:
  //   /widget/:slug          — the shareable link (same slugs as the guest portal)
  //   /widget?restaurantId=N — the original form, kept because restaurants have
  //                            already embedded it in iframes on their own sites.
  const { slug } = useParams();
  const queryRestaurantId = searchParams.get('restaurantId');
  const [slugRestaurantId, setSlugRestaurantId] = useState(null);
  const [slugNotFound, setSlugNotFound] = useState(false);
  const restaurantId = slug ? slugRestaurantId : queryRestaurantId;
  // True while we're turning the slug into an id — the "missing restaurant"
  // error must not flash during that round-trip.
  const resolvingSlug = !!slug && !slugRestaurantId && !slugNotFound;

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setSlugNotFound(false);
    fetchRestaurantBySlug(slug)
      .then((r) => {
        if (cancelled) return;
        if (r?.id) setSlugRestaurantId(String(r.id));
        else setSlugNotFound(true);
      })
      .catch(() => { if (!cancelled) setSlugNotFound(true); });
    return () => { cancelled = true; };
  }, [slug]);

  const eventId = searchParams.get('eventId');
  const shiftId = searchParams.get('shiftId');
  // Host-embed mode (opt-in). Only set by trusted hosts that size the iframe via
  // the resize messages below; plain external embeds leave it off and are unchanged.
  const embedded = searchParams.get('embed') === '1';
  // JWT handed in by the host (e.g. a signed-in Kiki's user). The widget runs in a
  // cross-origin iframe, so AuthContext can't see the host's localStorage — we use
  // this token to fetch the profile and prefill the form's contact fields.
  const token = searchParams.get('token');

  useEffect(() => {
    if (resolvingSlug) return;   // wait for the slug lookup
    if (!restaurantId) {
      setError(slug ? 'Restaurant not found' : 'Restaurant ID is required');
      setLoading(false);
      return;
    }

    const loadRestaurant = async () => {
      try {
        setLoading(true);
        const data = await getRestaurantById(restaurantId);
        setRestaurant(data);
        setError(null);
      } catch (err) {
        console.error('Error loading restaurant:', err);
        setError('Failed to load restaurant information');
      } finally {
        setLoading(false);
      }
    };

    loadRestaurant();
  }, [restaurantId, resolvingSlug, slug]);

  // Per-page SEO once the restaurant is known. The canonical is always the slug
  // link, also for the legacy ?restaurantId= form (same page, same restaurant);
  // a restaurant without a slug gets no canonical rather than the bare /widget.
  useEffect(() => {
    if (!restaurant?.name) return;
    setPageSeo({
      title: `Reserve a table at ${restaurant.name} | ez restaurants`,
      description: `Book a table at ${restaurant.name} online — pick a date, time and party size and get an instant confirmation.`,
      canonicalPath: slug || restaurant.slug ? `/widget/${slug || restaurant.slug}` : undefined,
    });
  }, [restaurant, slug]);

  // Fetch event if eventId is provided in URL
  useEffect(() => {
    if (!eventId) return;
    const loadEvent = async () => {
      try {
        const data = await getEventById(eventId);
        if (data.accessDenied) {
          setEventAccessDenied(data.message || 'This is a private experience. You don\'t have access.');
          setInitialEvent(null);
          return;
        }
        setEventAccessDenied(null);
        if (data.event) {
          setInitialEvent(data.event);
        }
      } catch (err) {
        console.error('Error loading event:', err);
      }
    };
    loadEvent();
  }, [eventId]);

  // Fetch event shift if shiftId is provided in URL
  useEffect(() => {
    if (!shiftId) return;
    const loadShift = async () => {
      try {
        const data = await getShiftById(shiftId);
        if (data) {
          setInitialEventShift(data);
          setDisplayEventShift(data);
        }
      } catch (err) {
        console.error('Error loading shift:', err);
      }
    };
    loadShift();
  }, [shiftId]);

  // Fetch the signed-in user's profile (when a host token is provided) so the
  // form can prefill First/Last name, Email and Phone. Best-effort: on any
  // failure the form simply stays blank and works as a normal guest booking.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const loadProfile = async () => {
      try {
        const res = await axios.get(`${BASE_API}/users/profile/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const body = res.data?.data ?? res.data;
        const u = body?.user ?? body;
        if (!u) return;
        setPrefillUser({
          firstName: u.firstName || '',
          lastName: u.lastName || '',
          email: u.email || '',
          phone: u.phoneNumber || u.phone || '',
        });
      } catch (err) {
        // Silent — prefill is a convenience, not a requirement.
      }
    };
    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // In host-embed mode, blend into the host page by dropping the standalone
  // page's grey background so the host's own surface shows through.
  useEffect(() => {
    if (!embedded || typeof document === 'undefined') return;
    document.documentElement.style.background = 'transparent';
    document.body.style.background = 'transparent';
  }, [embedded]);

  // Auto-resize: tell the host iframe how tall the content is so it can size the
  // frame to fit (no nested scrollbar). Only active in embed mode. Measures the
  // wrapper, which in embed mode has its min-height:100vh removed so the height
  // reflects content, not the iframe — avoiding a grow-on-every-post feedback loop.
  useEffect(() => {
    if (!embedded || typeof window === 'undefined' || !('ResizeObserver' in window)) return;
    const target = document.querySelector('.reservation-widget-wrapper');
    if (!target) return;
    let lastSent = 0;
    const sendHeight = () => {
      const h = Math.ceil(target.getBoundingClientRect().height);
      if (h > 0 && Math.abs(h - lastSent) > 2) {
        lastSent = h;
        post({ type: 'resize', height: h });
      }
    };
    const ro = new ResizeObserver(sendHeight);
    ro.observe(target);
    sendHeight();
    return () => ro.disconnect();
  }, [embedded, restaurant, loading, error, showSuccess, isEventDate, displayEventShift, eventAccessDenied]);

  // Notify the host when a reservation completes.
  useEffect(() => {
    if (embedded && showSuccess) post({ type: 'submitted' });
  }, [embedded, showSuccess]);

  if (resolvingSlug) {
    return (
      <div className="widget-loading-container">
        <div className="widget-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!restaurantId) {
    return (
      <div className="widget-error-container">
        <p className="widget-error-message">
          {slug ? 'We could not find that restaurant.' : 'Missing restaurant. Use /widget/<name> or add ?restaurantId= to the URL.'}
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="widget-loading-container">
        <div className="widget-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="widget-error-container">
        <p className="widget-error-message">{error}</p>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="widget-error-container">
        <p className="widget-error-message">Restaurant not found</p>
      </div>
    );
  }

  if (eventAccessDenied) {
    const isLoggedIn = !!localStorage.getItem('eez7_client_token');
    return (
      <div className="widget-error-container" style={{ textAlign: 'center', padding: '3rem 1.5rem' }}>
        <div style={{
          fontSize: '3rem',
          marginBottom: '1rem',
          color: '#6f42c1',
        }}>
          <i className="bi bi-lock-fill"></i>
        </div>
        <h3 style={{ marginBottom: '0.75rem' }}>Private Experience</h3>
        <p className="widget-error-message" style={{ maxWidth: 480, margin: '0 auto 1rem' }}>
          {eventAccessDenied}
        </p>
        {!isLoggedIn && (
          <a
            href={`/login?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`}
            className="btn btn-primary"
            style={{ marginTop: '0.5rem' }}
          >
            Sign in to check access
          </a>
        )}
      </div>
    );
  }

  // Build custom color styles: query-param overrides (host theming) take
  // precedence, then the restaurant's own widget settings, then hard defaults.
  const themeColor = (param, restaurantValue, fallback) =>
    searchParams.get(param) || restaurantValue || fallback;
  const customColorStyles = {
    '--primary-purple': themeColor('primaryColor', restaurant.widgetPrimaryColor, '#1A1A1A'),
    '--secondary-purple': themeColor('secondaryColor', restaurant.widgetSecondaryColor, '#2D2D2D'),
    '--accent-lime': themeColor('accentColor', restaurant.widgetAccentColor, '#BFFF00'),
    '--text-dark': themeColor('textColor', restaurant.widgetTextColor, '#1a1a1a'),
    '--white': themeColor('backgroundColor', restaurant.widgetBackgroundColor, '#ffffff'),
    // Derived colors for consistency
    '--light-purple': themeColor('secondaryColor', restaurant.widgetSecondaryColor, '#333333'),
    '--bg-light': themeColor('backgroundColor', restaurant.widgetBackgroundColor, '#f9f9f9'),
  };

  return (
    <div
      className={`reservation-widget-wrapper${embedded ? ' is-embedded' : ''}`}
      style={customColorStyles}
    >
      {/* Restaurant Image Header */}
      {restaurant.imageUrl && (
        <div className="widget-restaurant-header">
          <div className="widget-restaurant-image-wrapper">
            <img
              src={`${process.env.REACT_APP_BACKEND_URL || ''}${restaurant.imageUrl}`}
              alt={restaurant.name}
              className="widget-restaurant-image"
            />
            {(restaurant.location || (restaurant.phoneNumbers && restaurant.phoneNumbers.length > 0)) && (
              <div className="widget-restaurant-overlay">
                {restaurant.location && (
                  <p className="widget-restaurant-location">
                    <i className="bi bi-geo-alt-fill"></i> {restaurant.location}
                  </p>
                )}
                {restaurant.phoneNumbers && restaurant.phoneNumbers.length > 0 && (
                  <div className="widget-restaurant-contacts">
                    {restaurant.phoneNumbers.map((phone, index) => (
                      <a key={index} href={`tel:${phone}`} className="widget-restaurant-phone">
                        <i className="bi bi-telephone-fill"></i> {phone}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Widget Note */}
      {restaurant.widgetNote && restaurant.widgetNote.replace(/<[^>]*>/g, '').trim() && !showSuccess && (!isEventDate || bookingChoice === 'regular') && (
        <div className="widget-note-container">
          <div
            className="widget-note-text"
            dangerouslySetInnerHTML={{ __html: normalizeNoteHtml(restaurant.widgetNote) }}
          />
        </div>
      )}
      {/* Event House Rules - shown only on event dates */}
      {eventHouseRules && eventHouseRules.replace(/<[^>]*>/g, '').trim() && !showSuccess && (
        <div className="widget-note-container">
          <div
            className="widget-note-text"
            dangerouslySetInnerHTML={{ __html: normalizeNoteHtml(eventHouseRules) }}
          />
        </div>
      )}
      {/* Event Shift hero banner — image + description for shifts opened via copy-link */}
      {displayEventShift && displayEventShift.shiftType === 'event' && !showSuccess && (
        <div
          className="widget-event-shift-banner"
          style={{ maxWidth: 650, margin: '0 auto 1rem', padding: '0 1rem', width: '100%', boxSizing: 'border-box' }}
        >
          {displayEventShift.imageUrl && (
            <div
              style={{
                width: '100%',
                height: 220,
                borderRadius: 12,
                overflow: 'hidden',
                boxShadow: '0 4px 15px rgba(26, 26, 26, 0.15)',
              }}
            >
              <img
                src={`${process.env.REACT_APP_BACKEND_URL || ''}${displayEventShift.imageUrl}`}
                alt={displayEventShift.label}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </div>
          )}
          <h4 style={{ marginTop: 12, marginBottom: 4 }}>{displayEventShift.label}</h4>
          {displayEventShift.description && (
            <p style={{ whiteSpace: 'pre-wrap', color: '#444', margin: 0 }}>{displayEventShift.description}</p>
          )}
        </div>
      )}
      <ReservationForm
        restaurantId={restaurantId}
        restaurantName={restaurant.name}
        restaurant={restaurant}
        isWidget={true}
        prefillUser={prefillUser}
        /* Host-authenticated = the Kiki's embed handed us a valid JWT that
           resolved to a profile. Used to skip the guest email OTP for logged-in
           Kiki's users; plain widget embeds (no host token) keep verifying. */
        hostAuthenticated={embedded && !!prefillUser}
        initialEvent={initialEvent}
        onInitialEventHandled={() => setInitialEvent(null)}
        initialEventShift={initialEventShift}
        onInitialEventShiftHandled={() => setInitialEventShift(null)}
        onSuccessChange={setShowSuccess}
        onEventDateChange={setIsEventDate}
        onEventHouseRulesChange={setEventHouseRules}
        onBookingChoiceChange={setBookingChoice}
      />
      <PoweredBy hidden={!!restaurant?.organization?.hidePoweredBy} />
    </div>
  );
};

export default ReservationWidget;
